import { DEMO_STATE_KEY } from './runtime.js';

let inMemoryState = null;

const getSessionStorage = () => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const nowIso = () => new Date().toISOString();
const UI_VERSION_VALUES = new Set([1, 2, 3]);

const deepClone = (value) => JSON.parse(JSON.stringify(value));

const normalizeUiVersion = (value) => {
  if (typeof value === 'number' && Number.isInteger(value) && UI_VERSION_VALUES.has(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed) {
      const parsed = Number(trimmed);
      if (Number.isInteger(parsed) && UI_VERSION_VALUES.has(parsed)) {
        return parsed;
      }
    }
  }
  return 1;
};

const normalizeDemoState = (state) => {
  if (!state || typeof state !== 'object') return state;
  if (!Array.isArray(state.users)) return state;
  return {
    ...state,
    users: state.users.map((user) => ({
      ...user,
      uiVersion: normalizeUiVersion(user?.uiVersion),
    })),
  };
};

const createDemoUser = () => ({
  email: 'demo@anforderungsai.de',
  uiVersion: 1,
  role: 'user',
  status: 'active',
  planId: 'unlimited',
  billingStatus: 'active',
  stripeCustomerId: 'cus_demo_0001',
  stripeSubscriptionId: 'sub_demo_0001',
  stripeSubscriptionItemId: 'si_demo_0001',
  stripePriceId: 'price_demo_unlimited',
  billing: {
    method: 'card',
    brand: 'visa',
    last4: '4242',
    expMonth: 12,
    expYear: 2030,
  },
  profile: {
    firstName: 'Demo',
    lastName: 'Account',
    company: 'Nordachse Mobility',
    phone: '+49 170 0000000',
    vatNumber: 'DE999999999',
    address: {
      street: 'Demo-Straße 1',
      postalCode: '10115',
      city: 'Berlin',
      country: 'Deutschland',
    },
  },
  jobStats: {
    total: 3,
    currentMonth: 2,
    monthly: [
      { month: '2026-01', count: 1 },
      { month: '2026-02', count: 2 },
    ],
  },
  createdAt: '2026-01-01T10:00:00.000Z',
  updatedAt: nowIso(),
});

const createDemoConversations = () => ([
  {
    conversationId: 'demo-conv-ladehub',
    customerId: 'Nordachse Mobility',
    projectId: 'Ladehub EU',
    meetingDate: '2026-01-27',
    status: 'READY',
    mode: 'upload',
    originalFilename: 'workshop_ladehub.webm',
    transcriptionEngine: 'mistral',
    createdAt: '2026-01-27T09:10:00.000Z',
    updatedAt: '2026-01-27T09:23:00.000Z',
    changeLog: [],
  },
  {
    conversationId: 'demo-conv-portal',
    customerId: 'Futura Health',
    projectId: 'Patientenportal',
    meetingDate: '2026-01-29',
    status: 'READY',
    mode: 'transcript',
    originalFilename: 'interview_patientenportal.pdf',
    transcriptionEngine: 'aws-transcribe',
    createdAt: '2026-01-29T15:05:00.000Z',
    updatedAt: '2026-01-29T15:19:00.000Z',
    changeLog: [],
  },
  {
    conversationId: 'demo-conv-bauzeiten',
    customerId: 'Musterbau GmbH',
    projectId: 'Bauzeiten-App',
    meetingDate: '2026-02-03',
    status: 'READY',
    mode: 'upload',
    originalFilename: 'kickoff_bauzeiten.mp3',
    transcriptionEngine: 'mistral',
    createdAt: '2026-02-03T11:40:00.000Z',
    updatedAt: '2026-02-03T11:55:00.000Z',
    changeLog: [],
  },
]);

const attachMeta = (items, conversation) =>
  items.map((item) => ({
    ...item,
    conversationId: conversation.conversationId,
    customerId: conversation.customerId,
    projectId: conversation.projectId,
    meetingDate: conversation.meetingDate,
  }));

const createSeedResults = (conversations) => {
  const byId = {};

  const ladehubRequirements = attachMeta(
    [
      {
        sortKey: 'R-001',
        type: 'Funktional',
        priority: 'must',
        status: 'OPEN',
        text: 'Der Ladehub muss modulare Stationen unterstützen (Austausch in < 10 Minuten).',
        rationale: 'Skalierung je Standort, Wartung ohne Stillstand.',
        categories: ['Betrieb', 'Modularität'],
        tags: ['ladehub', 'hardware'],
        confidence: 0.86,
        sourceRange: { startSec: 42.1, endSec: 58.4, speaker: 'PM' },
      },
      {
        sortKey: 'R-002',
        type: 'Performance',
        priority: 'must',
        status: 'BACKLOG',
        text: 'Bei Peak-Verbrauch muss das Lastmanagement innerhalb von 2 Minuten reagieren.',
        rationale: 'Vermeidet Überlast und schützt Infrastruktur.',
        categories: ['Performance'],
        tags: ['lastmanagement'],
        confidence: 0.8,
        sourceRange: { startSec: 73.0, endSec: 86.6, speaker: 'Tech Lead' },
      },
      {
        sortKey: 'R-003',
        type: 'Reporting',
        priority: 'should',
        status: 'NEW',
        text: 'Monatliche Abrechnungsreports sollen als CSV und PDF exportierbar sein.',
        categories: ['Export'],
        tags: ['billing'],
        confidence: 0.72,
        sourceRange: { startSec: 120.4, endSec: 136.8, speaker: 'Finance' },
      },
      {
        sortKey: 'R-004',
        type: 'Security',
        priority: 'must',
        status: 'IN_PROGRESS',
        text: 'Zugriff auf Admin-Funktionen nur über rollenbasierte Rechte (RBAC).',
        categories: ['Security'],
        tags: ['rbac'],
        confidence: 0.78,
        sourceRange: { startSec: 180.2, endSec: 194.4, speaker: 'Security' },
      },
    ],
    conversations[0],
  );

  const ladehubStories = attachMeta(
    [
      {
        id: 'US-001',
        sortKey: 'US-001',
        priority: 'must',
        categories: ['Betrieb'],
        story: 'Als Standortmanager möchte ich Stationen modular tauschen können, damit Wartungen ohne Ausfall möglich sind.',
        role: 'Standortmanager',
        goal: 'Stationen tauschen',
        benefit: 'geringere Ausfallzeit',
        acceptance: [
          'Station kann in < 10 Minuten ausgetauscht werden.',
          'System erkennt neue Station automatisch.',
        ],
        sourceRange: { startSec: 210.1, endSec: 238.2, speaker: 'PM' },
      },
    ],
    conversations[0],
  );

  const portalRequirements = attachMeta(
    [
      {
        sortKey: 'R-101',
        type: 'Security',
        priority: 'must',
        status: 'IN_PROGRESS',
        text: 'Login muss 2FA (TOTP) unterstützen.',
        categories: ['Security'],
        tags: ['2fa', 'totp'],
        confidence: 0.9,
        sourceRange: { startSec: 33.2, endSec: 49.0, speaker: 'IT' },
      },
      {
        sortKey: 'R-102',
        type: 'UX',
        priority: 'should',
        status: 'OPEN',
        text: 'Terminübersicht soll mobil-first sein und barrierearm (WCAG AA).',
        categories: ['UX'],
        tags: ['a11y', 'mobile'],
        confidence: 0.76,
        sourceRange: { startSec: 88.0, endSec: 107.3, speaker: 'UX' },
      },
      {
        sortKey: 'R-103',
        type: 'Integration',
        priority: 'must',
        status: 'BACKLOG',
        text: 'FHIR-Schnittstelle für Patientenstammdaten (Read-only) ist erforderlich.',
        categories: ['Integration'],
        tags: ['fhir', 'api'],
        confidence: 0.82,
        sourceRange: { startSec: 140.4, endSec: 158.9, speaker: 'Architekt' },
      },
    ],
    conversations[1],
  );

  const portalStories = attachMeta(
    [
      {
        id: 'US-101',
        sortKey: 'US-101',
        priority: 'should',
        categories: ['UX'],
        story: 'Als Patient möchte ich Termine schnell auf dem Smartphone finden, damit ich nichts verpasse.',
        role: 'Patient',
        goal: 'Termine finden',
        benefit: 'Übersicht',
        acceptance: ['Startseite zeigt nächste 3 Termine.', 'Kontrast erfüllt WCAG AA.'],
        sourceRange: { startSec: 170.0, endSec: 192.0, speaker: 'UX' },
      },
    ],
    conversations[1],
  );

  const bauzeitenRequirements = attachMeta(
    [
      {
        sortKey: 'R-201',
        type: 'Funktional',
        priority: 'must',
        status: 'OPEN',
        text: 'Bauleiter müssen Bauabschnitte per Drag & Drop umplanen können.',
        categories: ['Planung'],
        tags: ['gantt'],
        confidence: 0.79,
        sourceRange: { startSec: 21.5, endSec: 40.9, speaker: 'Bauleitung' },
      },
      {
        sortKey: 'R-202',
        type: 'Offline',
        priority: 'should',
        status: 'BACKLOG',
        text: 'Die App soll offline arbeiten und bei Verbindung synchronisieren.',
        categories: ['Offline'],
        tags: ['sync'],
        confidence: 0.7,
        sourceRange: { startSec: 65.0, endSec: 79.2, speaker: 'Bauleitung' },
      },
      {
        sortKey: 'R-203',
        type: 'Audit',
        priority: 'must',
        status: 'NEW',
        text: 'Änderungen am Bauzeitenplan müssen revisionssicher protokolliert werden.',
        categories: ['Compliance'],
        tags: ['audit'],
        confidence: 0.77,
        sourceRange: { startSec: 92.4, endSec: 110.0, speaker: 'QM' },
      },
    ],
    conversations[2],
  );

  const bauzeitenStories = attachMeta(
    [
      {
        id: 'US-201',
        sortKey: 'US-201',
        priority: 'must',
        categories: ['Planung'],
        story: 'Als Bauleiter möchte ich Abschnitte flexibel umplanen, damit Änderungen vor Ort schnell sichtbar sind.',
        role: 'Bauleiter',
        goal: 'Umplanen',
        benefit: 'weniger Abstimmungsschleifen',
        acceptance: ['Drag & Drop auf mobilen Geräten möglich.', 'Änderungen werden synchronisiert.'],
        sourceRange: { startSec: 120.0, endSec: 145.0, speaker: 'Bauleitung' },
      },
    ],
    conversations[2],
  );

  const resultsFor = (conversation, requirements, userStories) => ({
    conversation,
    requirements,
    userStories,
  });

  byId[conversations[0].conversationId] = resultsFor(conversations[0], ladehubRequirements, ladehubStories);
  byId[conversations[1].conversationId] = resultsFor(conversations[1], portalRequirements, portalStories);
  byId[conversations[2].conversationId] = resultsFor(conversations[2], bauzeitenRequirements, bauzeitenStories);

  return byId;
};

const createSystemTemplates = () => {
  const requirementFields = [
    { sourceField: 'type', targetName: 'Typ', enabled: true, order: 1 },
    { sourceField: 'text', targetName: 'Text', enabled: true, order: 2 },
    { sourceField: 'priority', targetName: 'Priorität', enabled: true, order: 3 },
    { sourceField: 'status', targetName: 'Status', enabled: true, order: 4 },
    { sourceField: 'categories', targetName: 'Kategorien', enabled: true, order: 5 },
    { sourceField: 'tags', targetName: 'Tags', enabled: false, order: 6 },
    { sourceField: 'sourceRange', targetName: 'Quelle', enabled: true, order: 7 },
  ];
  const userStoryFields = [
    { sourceField: 'story', targetName: 'Story', enabled: true, order: 1 },
    { sourceField: 'role', targetName: 'Rolle', enabled: true, order: 2 },
    { sourceField: 'goal', targetName: 'Ziel', enabled: true, order: 3 },
    { sourceField: 'benefit', targetName: 'Nutzen', enabled: true, order: 4 },
    { sourceField: 'acceptance', targetName: 'Akzeptanz', enabled: true, order: 5 },
  ];

  const baseConfig = {
    fields: requirementFields,
    userStoryFields,
    filters: {},
    formatOptions: {},
  };

  return [
    {
      templateId: 'sys-csv-standard',
      name: 'Standard CSV',
      format: 'csv',
      isDefault: true,
      config: {
        ...deepClone(baseConfig),
        formatOptions: { delimiter: ',', encoding: 'utf-8', includeHeaders: true },
      },
    },
    {
      templateId: 'sys-json-standard',
      name: 'Standard JSON',
      format: 'json',
      isDefault: true,
      config: {
        ...deepClone(baseConfig),
        formatOptions: { prettyPrint: true, rootKey: 'data', arrayWrapper: true },
      },
    },
    {
      templateId: 'sys-md-standard',
      name: 'Standard Markdown',
      format: 'md',
      isDefault: true,
      config: {
        ...deepClone(baseConfig),
        formatOptions: { includeTableOfContents: true, headingLevel: 2 },
      },
    },
    {
      templateId: 'sys-xlsx-standard',
      name: 'Standard Excel',
      format: 'xlsx',
      isDefault: true,
      config: deepClone(baseConfig),
    },
    {
      templateId: 'sys-docx-standard',
      name: 'Standard Word',
      format: 'docx',
      isDefault: true,
      config: deepClone(baseConfig),
    },
  ];
};

const createSeedBilling = () => ({
  invoices: [
    {
      created: 1736899200, // 2025-01-15
      description: 'Paket M · 10 Jobs',
      amount: 1000,
      currency: 'eur',
      status: 'paid',
      invoice_pdf: null,
      plan_name: 'package_m',
    },
    {
      created: 1739577600, // 2025-02-15
      description: 'Unlimited',
      amount: 6000,
      currency: 'eur',
      status: 'paid',
      invoice_pdf: null,
      plan_name: 'unlimited',
    },
  ],
  paymentMethods: [
    {
      id: 'pm_demo_visa',
      brand: 'visa',
      last4: '4242',
      expMonth: 12,
      expYear: 2030,
      method: 'card',
      isDefault: true,
    },
    {
      id: 'pm_demo_mastercard',
      brand: 'mastercard',
      last4: '4444',
      expMonth: 6,
      expYear: 2029,
      method: 'card',
      isDefault: false,
    },
  ],
  history: [],
});

const createSeedAppFactory = () => {
  const baseSession = {
    sessionId: 'demo-af-001',
    status: 'in_progress',
    guidelineId: 'default-v1',
    displayName: 'Pilot · Ladehub EU',
    customerId: 'Nordachse Mobility',
    projectId: 'Ladehub EU',
    ownerEmail: 'demo@anforderungsai.de',
    createdAt: '2026-02-02T08:30:00.000Z',
    progress: {
      percentComplete: 33,
      completedCategories: 1,
      totalCategories: 3,
      categories: [
        {
          name: 'Ziele & Nutzer',
          completed: true,
          confidence: 0.9,
          questions: [
            { text: 'Wer nutzt die Lösung?', answered: true, extractedInfo: 'Betriebsteam & Standortmanager' },
            { text: 'Was ist das Hauptziel?', answered: true, extractedInfo: 'Skalierbarer Ladebetrieb' },
          ],
        },
        {
          name: 'Funktionen',
          completed: false,
          confidence: 0.6,
          questions: [
            { text: 'Welche Kernfunktionen sind nötig?', answered: false },
            { text: 'Welche Integrationen braucht es?', answered: false },
          ],
        },
        {
          name: 'Nicht-funktionale Anforderungen',
          completed: false,
          confidence: 0.5,
          questions: [
            { text: 'Performance- & SLA-Ziele?', answered: false },
            { text: 'Security & Compliance?', answered: false },
          ],
        },
      ],
    },
    extractedRequirements: [
      { type: 'Funktional', text: 'Stationen modular austauschbar', priority: 'high' },
      { type: 'Nicht-funktional', text: 'Lastmanagement reagiert in < 2 Minuten', priority: 'high' },
    ],
  };

  return {
    sessions: [baseSession],
  };
};

const seedDemoState = () => {
  const demoUser = createDemoUser();
  const conversations = createDemoConversations();
  const resultsByConversationId = createSeedResults(conversations);
  return {
    version: 1,
    createdAt: nowIso(),
    currentUserEmail: demoUser.email,
    users: [
      demoUser,
      {
        email: 'sarah.mueller@example.com',
        uiVersion: 1,
        role: 'user',
        status: 'active',
        planId: 'package_m',
        billingStatus: 'active',
        profile: {
          firstName: 'Sarah',
          lastName: 'Müller',
          company: 'Futura Health',
          phone: '+49 30 123456',
          vatNumber: '',
          address: {
            street: 'Musterweg 12',
            postalCode: '20095',
            city: 'Hamburg',
            country: 'Deutschland',
          },
        },
        jobStats: { total: 1, currentMonth: 0, monthly: [{ month: '2026-01', count: 1 }] },
        createdAt: '2026-01-10T09:00:00.000Z',
        updatedAt: nowIso(),
      },
    ],
    conversations,
    resultsByConversationId,
    exportTemplates: {
      systemTemplates: createSystemTemplates(),
      userTemplates: [],
    },
    billing: createSeedBilling(),
    appFactory: createSeedAppFactory(),
    live: {
      sessionsById: {},
    },
    releaseNotes: {
      latestReleaseId: null,
      releases: [],
      acks: {},
    },
  };
};

const parseState = (raw) => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== 1) return null;
    return parsed;
  } catch {
    return null;
  }
};

export const loadDemoState = ({ createIfMissing = true } = {}) => {
  const storage = getSessionStorage();
  if (!storage) {
    if (!inMemoryState && createIfMissing) {
      inMemoryState = seedDemoState();
    }
    return inMemoryState;
  }

  const raw = storage.getItem(DEMO_STATE_KEY);
  const parsed = parseState(raw);
  if (parsed) return normalizeDemoState(parsed);
  if (!createIfMissing) return null;

  const seeded = seedDemoState();
  storage.setItem(DEMO_STATE_KEY, JSON.stringify(seeded));
  return normalizeDemoState(seeded);
};

export const saveDemoState = (state) => {
  if (!state) return;
  const storage = getSessionStorage();
  if (!storage) {
    inMemoryState = deepClone(state);
    return;
  }
  try {
    storage.setItem(DEMO_STATE_KEY, JSON.stringify(state));
  } catch (error) {
    console.warn('Failed to persist demo state', error);
    inMemoryState = deepClone(state);
  }
};

export const resetDemoState = () => {
  const storage = getSessionStorage();
  if (!storage) {
    inMemoryState = null;
    return;
  }
  storage.removeItem(DEMO_STATE_KEY);
};

export const ensureDemoState = () => {
  return loadDemoState({ createIfMissing: true });
};
